
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Send, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import EnhancedInput from '@/components/ui/enhanced-input';
import { enhancedToast } from '@/components/ui/enhanced-toast';
import { trackConversion } from '@/utils/analytics';
import { sanitizeEmail, sanitizeUserInput, rateLimitCheck } from '@/utils/security';
import emailjs from '@emailjs/browser';

const contactSchema = z.object({
  name: z.string()
    .min(2, 'Imię musi mieć co najmniej 2 znaki')
    .max(50, 'Imię może mieć maksymalnie 50 znaków')
    .regex(/^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ\s-]+$/, 'Imię może zawierać tylko litery'),
  email: z.string()
    .email('Podaj prawidłowy adres email')
    .max(254, 'Adres email jest zbyt długi')
    .refine((email) => {
      try {
        sanitizeEmail(email);
        return true;
      } catch {
        return false;
      }
    }, 'Nieprawidłowy format adresu email'),
  phone: z.string()
    .min(9, 'Podaj prawidłowy numer telefonu')
    .max(15, 'Numer telefonu jest zbyt długi')
    .regex(/^[\d\s+()-]+$/, 'Numer telefonu może zawierać tylko cyfry i znaki: +()-'),
  message: z.string()
    .min(10, 'Wiadomość musi mieć co najmniej 10 znaków')
    .max(1000, 'Wiadomość może mieć maksymalnie 1000 znaków'),
});

type ContactFormData = z.infer<typeof contactSchema>;

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    mode: 'onChange'
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    try {
      // Rate limiting check
      if (!rateLimitCheck('contact_form', 300000, 3)) { // 5 minutes, max 3 requests
        enhancedToast.error(
          'Zbyt wiele próśb',
          {
            description: 'Spróbuj ponownie za 5 minut.',
            duration: 5000,
          }
        );
        return;
      }

      // Sanitize input data before sending
      const sanitizedData = {
        to_name: '061 Garage',
        to_email: '061garageds@gmail.com',
        from_name: sanitizeUserInput(data.name),
        from_email: sanitizeEmail(data.email),
        phone: sanitizeUserInput(data.phone),
        message: sanitizeUserInput(data.message),
      };

      // Track form submission attempt
      trackConversion.contactForm('main_contact');

      await emailjs.send(
        'service_q9jsj95',
        'template_dzfbol8',
        sanitizedData,
        'saalZk4vkgKfxP6xN'
      );

      enhancedToast.success(
        'Wiadomość została wysłana!',
        {
          description: 'Odpowiemy w ciągu 24 godzin.',
          duration: 6000,
        }
      );

      // Track successful submission
      trackConversion.contactForm('main_contact_success');
      
      reset();
    } catch (error) {
      // Error handled by toast - no console.error needed in production
      
      enhancedToast.error(
        'Wystąpił błąd przy wysyłaniu',
        {
          description: 'Spróbuj ponownie lub zadzwoń bezpośrednio.',
          action: {
            label: 'Zadzwoń',
            onClick: () => {
              window.location.href = 'tel:+48602709327';
              trackConversion.phoneCall('+48602709327');
            }
          }
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneClick = () => {
    trackConversion.phoneCall('+48602709327');
  };

  return (
    <div className="max-w-2xl mx-auto">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-black border border-white/20 p-6 md:p-8 rounded-lg">
        <div className="grid md:grid-cols-2 gap-6">
          <EnhancedInput
            {...register('name')}
            label="Imię i nazwisko"
            placeholder="Jan Kowalski"
            error={errors.name?.message}
            required
          />
          
          <EnhancedInput
            {...register('email')}
            type="email"
            label="Adres email"
            placeholder="jan@example.com"
            error={errors.email?.message}
            required
          />
        </div>
        
        <EnhancedInput
          {...register('phone')}
          type="tel"
          label="Numer telefonu"
          placeholder="+48 123 456 789"
          error={errors.phone?.message}
          required
        />
        
        <div className="space-y-2">
          <label className="text-sm font-medium text-white">
            Wiadomość <span className="text-red-500">*</span>
          </label>
          <textarea
            {...register('message')}
            rows={5}
            className="flex w-full rounded-md border border-garage-blue bg-garage-blue text-white px-3 py-2 text-sm transition-all duration-200 placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-garage-blue/20 focus:border-garage-blue disabled:cursor-not-allowed disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-garage-blue focus-visible:border-garage-blue"
            placeholder="Opisz swoje potrzeby..."
            aria-describedby={errors.message ? "message-error" : undefined}
          />
          {errors.message && (
            <p id="message-error" className="text-xs text-red-500" role="alert">
              {errors.message.message}
            </p>
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            type="submit"
            disabled={!isValid || isSubmitting}
            className="flex-1 bg-garage-blue hover:bg-garage-blue/90 disabled:opacity-50"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Wysyłam...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4" />
                Wyślij wiadomość
              </div>
            )}
          </Button>
          
          <Button
            type="button"
            variant="outline"
            onClick={handlePhoneClick}
            className="flex items-center gap-2 border-garage-blue text-garage-blue hover:bg-garage-blue hover:text-white"
            asChild
          >
            <a href="tel:+48602709327">
              <Phone className="w-4 h-4" />
              Zadzwoń teraz
            </a>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
